import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalystBench, type IndicatorData, type CAMELScore } from '../../../context/AnalystBenchContext';
import { usePeriodData, useInstitutionDataAsync } from '../../../hooks/useAsyncInstitutionData';
import './LandingView.css';

// Component display name i18n keys
const COMPONENT_NAME_KEYS: Record<string, string> = {
  'C': 'camel.assetQuality',
  'S': 'camel.solvency',
  'L': 'camel.liquidity',
  'EG': 'camel.efficiency',
  'R': 'camel.profitability',
  'STI': 'camel.interestRate',
};

// Component weights (from CAMEL methodology)
const COMPONENT_WEIGHTS: Record<string, number> = {
  'C': 22.56,
  'S': 10.55,
  'L': 29.40,
  'EG': 9.95,
  'R': 7.91,
  'STI': 19.64,
};

/**
 * Generate insights from indicator data
 */
function generateInsights(indicators: IndicatorData[] | undefined): Array<{
  id: number;
  indicator: string;
  message: string;
  severity: 'positive' | 'warning' | 'neutral';
}> {
  if (!indicators || indicators.length === 0) return [];

  return indicators.slice(0, 4).map((ind, idx) => {
    const score = parseFloat(ind.indicator_score);
    const value = parseFloat(ind.indicator_value);
    const severity = score >= 70 ? 'positive' : score >= 40 ? 'neutral' : 'warning';

    // Format value as percentage if it's a ratio
    const formattedValue = value < 1 ? `${(value * 100).toFixed(1)}%` : value.toFixed(2);

    return {
      id: idx,
      indicator: ind.indicator_id,
      message: `${ind.indicator_name}: ${formattedValue} (Score: ${score.toFixed(0)})`,
      severity,
    };
  });
}

/**
 * Calculate score change between two periods
 */
function getScoreChange(currentScore: number, previousData: { finalScore: number } | null): {
  change: number;
  trend: 'up' | 'down' | 'stable';
} {
  if (!previousData) {
    return { change: 0, trend: 'stable' };
  }
  const change = currentScore - previousData.finalScore;
  return {
    change,
    trend: change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'stable',
  };
}

export default function LandingView() {
  const { t } = useTranslation();
  const { selectedInstitution, defaultPeriod, setActiveSection, setActiveRatioCategory } = useAnalystBench();

  // Fetch period-specific data using async hook
  const { data: periodData, isLoading: isPeriodLoading } = usePeriodData(
    selectedInstitution?.id,
    defaultPeriod
  );

  // Fetch institution data for history using async hook
  const { data: institutionData, isLoading: isInstitutionLoading } = useInstitutionDataAsync(
    selectedInstitution?.id
  );

  // Get previous period data for comparison
  const previousPeriodData = useMemo(() => {
    if (!institutionData?.history || !defaultPeriod) return null;

    // Find the period before the current one
    const sortedHistory = [...institutionData.history].sort((a, b) => b.date.localeCompare(a.date));
    const currentIndex = sortedHistory.findIndex(h => h.date === defaultPeriod);
    if (currentIndex >= 0 && currentIndex < sortedHistory.length - 1) {
      return sortedHistory[currentIndex + 1];
    }
    return null;
  }, [institutionData?.history, defaultPeriod]);

  // Calculate score change
  const scoreChange = useMemo(() => {
    if (!periodData) return { change: 0, trend: 'stable' as const };
    return getScoreChange(periodData.finalScore, previousPeriodData);
  }, [periodData, previousPeriodData]);

  // Generate insights from real indicator data
  const insights = useMemo(() => {
    return generateInsights(periodData?.sankeyIndicators);
  }, [periodData?.sankeyIndicators]);

  // Build component scores from CAMEL data
  const componentScores = useMemo(() => {
    if (!periodData?.camel) {
      // Fallback to empty/default
      return Object.entries(COMPONENT_NAME_KEYS).map(([id, key]) => ({
        id,
        name: t(key),
        score: 0,
        weight: COMPONENT_WEIGHTS[id] || 0,
        trend: 'stable' as const,
      }));
    }

    return periodData.camel.map((comp: CAMELScore) => {
      // Find previous period score for this component
      const prevComp = previousPeriodData?.camel?.find(c => c.code === comp.code);
      const scoreDiff = prevComp ? comp.score - prevComp.score : 0;
      const trend = scoreDiff > 0.5 ? 'up' : scoreDiff < -0.5 ? 'down' : 'stable';

      return {
        id: comp.code,
        name: COMPONENT_NAME_KEYS[comp.code] ? t(COMPONENT_NAME_KEYS[comp.code]) : comp.code,
        score: comp.score,
        weight: COMPONENT_WEIGHTS[comp.code] || 0,
        trend: trend as 'up' | 'down' | 'stable',
      };
    });
  }, [periodData?.camel, previousPeriodData?.camel, t]);

  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleInsightClick = (indicator: string) => {
    // Navigate to the relevant ratio section
    const firstChars = indicator.substring(0, 2);
    const categoryMap: Record<string, 'C' | 'S' | 'L' | 'EG' | 'R' | 'STI'> = {
      'C': 'C',
      'S': 'S',
      'L': 'L',
      'EG': 'EG',
      'R': 'R',
      'ST': 'STI',
    };
    // Check for EG and STI first (2 chars), then single char
    if (categoryMap[firstChars]) {
      setActiveSection('key-ratios');
      setActiveRatioCategory(categoryMap[firstChars]);
    } else if (categoryMap[indicator.charAt(0)]) {
      setActiveSection('key-ratios');
      setActiveRatioCategory(categoryMap[indicator.charAt(0)]);
    }
  };

  // Display values from real data or fallback
  const isLoading = isPeriodLoading || isInstitutionLoading;
  const displayRating = isLoading ? '...' : (periodData?.rating || '--');
  const displayScore = isLoading ? '...' : (periodData?.finalScore?.toFixed(2) || '--');
  const changeText = scoreChange.change !== 0
    ? `${scoreChange.change > 0 ? '+' : ''}${scoreChange.change.toFixed(1)} pts`
    : t('landing.noChange');

  return (
    <div className="landing-view">
      <div className="landing-header">
        <h1>{selectedInstitution?.name}</h1>
        <p className="landing-period">
          {defaultPeriod && formatPeriod(defaultPeriod)}
          {isLoading && <span className="loading-indicator"> {t('common.loading')}</span>}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="stat-card">
          <span className="stat-label">{t('landing.rating')}</span>
          <span className="stat-value rating-value">{displayRating}</span>
          <span className={`stat-change ${scoreChange.trend === 'up' ? 'positive' : scoreChange.trend === 'down' ? 'negative' : ''}`}>
            {scoreChange.trend === 'up' ? t('landing.improving') : scoreChange.trend === 'down' ? t('landing.declining') : t('landing.stable')}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('landing.score')}</span>
          <span className="stat-value">{displayScore}</span>
          <span className={`stat-change ${scoreChange.change > 0 ? 'positive' : scoreChange.change < 0 ? 'negative' : ''}`}>
            {changeText}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('landing.vsPeerGroup')}</span>
          <span className="stat-value">--</span>
          <span className="stat-change">{t('landing.comingSoon')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('landing.trend')}</span>
          <span className={`stat-value trend-${scoreChange.trend}`}>
            {scoreChange.trend === 'up' && (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3.33333 16.6667L10 10L13.3333 13.3333L16.6667 10M16.6667 10V14.1667M16.6667 10H12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {scoreChange.trend === 'down' && (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3.33333 3.33333L10 10L13.3333 6.66667L16.6667 10M16.6667 10V5.83333M16.6667 10H12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {scoreChange.trend === 'stable' && '→'}
            {' '}
            {scoreChange.trend === 'up' ? t('landing.improving') : scoreChange.trend === 'down' ? t('landing.declining') : t('landing.stable')}
          </span>
        </div>
      </div>

      {/* Period Insights */}
      <div className="insights-section">
        <div className="insights-header">
          <h2>
            <span className="insights-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6"/>
                <path d="M10 22h4"/>
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
              </svg>
            </span>
            {t('landing.keyIndicators')}
          </h2>
          <span className="insights-badge">{insights.length > 0 ? t('landing.liveData') : t('landing.noData')}</span>
        </div>

        <div className="insights-list">
          {insights.length > 0 ? (
            insights.map(insight => (
              <button
                key={insight.id}
                className={`insight-item ${insight.severity}`}
                onClick={() => handleInsightClick(insight.indicator)}
              >
                <span className={`insight-indicator ${insight.severity}`}>
                  {insight.indicator}
                </span>
                <span className="insight-message">{insight.message}</span>
                <svg className="insight-arrow" width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))
          ) : (
            <div className="no-data-message">
              {t('landing.noIndicators')}
            </div>
          )}
        </div>
      </div>

      {/* Component Summary */}
      <div className="components-summary">
        <h2>{t('landing.componentScores')}</h2>
        <div className="components-grid">
          {componentScores.map(component => (
            <button
              key={component.id}
              className="component-card"
              onClick={() => {
                setActiveSection('key-ratios');
                setActiveRatioCategory(component.id as 'C' | 'S' | 'L' | 'EG' | 'R' | 'STI');
              }}
            >
              <div className="component-header">
                <span className="component-id">{component.id}</span>
                <span className="component-name">{component.name}</span>
              </div>
              <div className="component-score">
                <span className="score-value">{component.score.toFixed(1)}</span>
                <span className={`score-trend ${component.trend}`}>
                  {component.trend === 'up' && '↑'}
                  {component.trend === 'down' && '↓'}
                  {component.trend === 'stable' && '→'}
                </span>
              </div>
              <div className="component-weight">{t('landing.weight', { value: component.weight.toFixed(2) })}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
